/**
 * Outil « Sections de solides » (Boîte à outils, 3e → 2nde).
 *
 * Un plan glisse dans un pavé droit, un cylindre, une pyramide, un cône ou une
 * boule. La figure suit les conventions du cours (arêtes cachées en
 * pointillés, la section en couleur, ses côtés vus en trait plein) et tourne
 * au doigt ; à côté, la section en vraie grandeur et le raisonnement
 * (superposable à une face, Pythagore, coefficient de réduction k, k³ × V).
 */

import { useMemo, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { Note, ParamSlider, ToggleChip } from '../../ui/controls';
import { formatNumber, frenchSpaces } from '../../ui/format';
import {
  add,
  dot,
  edgeLines,
  isFront,
  lerp3,
  normalize,
  scale,
  sectionPieces,
  sectionPolygon,
  sub,
  type Camera,
  type V3,
} from '../../ui/space3d';
import { FIGURE_BG_CLASS, HaloText, INK, MUTED } from '../../ui/svg-parts';
import { ORBIT_FOCUS_CLASS, OrbitButtons, pointsAttr, projector, useOrbit } from '../../ui/view3d';
import {
  CUTS,
  DIM,
  SECTION_SOLIDS,
  meshOf,
  namedPoints,
  paramOf,
  planeOf,
  sectionInfo,
  type Cut,
  type SectionSolid,
  type Shape,
} from './sections';

const SIZE = 320;
const SECTION_FILL = '#f59e0b';
const SECTION_LINE = '#b45309';
const SOLID_FILL = '#93c5fd';
const HELP = 'var(--color-primary-500)';

const OF: Record<SectionSolid, string> = {
  cuboid: 'du pavé droit',
  cylinder: 'du cylindre',
  pyramid: 'de la pyramide',
  cone: 'du cône',
  sphere: 'de la boule',
};

const BOUNDS: Record<SectionSolid, { c: V3; R: number }> = {
  cuboid: { c: [3, 2, 1.5], R: 3.95 },
  cylinder: { c: [0, 0, 2.5], R: 3.95 },
  pyramid: { c: [0, 0, 2.6], R: 5 },
  cone: { c: [0, 0, 2.8], R: 4.2 },
  sphere: { c: [0, 0, 0], R: 5.2 },
};

type XY = [number, number];
const seg = (a: XY, b: XY) =>
  `M${a[0].toFixed(1)},${a[1].toFixed(1)}L${b[0].toFixed(1)},${b[1].toFixed(1)}`;

/** petit carré d'angle droit en H, dans le plan (H, A, B) */
function rightMark(H: V3, A: V3, B: V3, size = 0.35): [V3, V3, V3] {
  const u = scale(normalize(sub(A, H)), size);
  const v = scale(normalize(sub(B, H)), size);
  return [add(H, u), add(add(H, u), v), add(H, v)];
}

/** direction horizontale « à droite de l'écran » */
const screenRight = (cam: Camera): V3 => normalize([cam.right[0], cam.right[1], 0]);

interface Label {
  text: string;
  at: V3;
}

function Labels({
  labels,
  proj,
  center,
}: {
  labels: readonly Label[];
  proj: (p: V3) => XY;
  center: XY;
}) {
  return (
    <g fontSize={13} fontWeight={700} fill={INK} textAnchor="middle" dominantBaseline="central">
      {labels.map((l) => {
        const [x, y] = proj(l.at);
        const dx = x - center[0];
        const dy = y - center[1];
        const d = Math.hypot(dx, dy) || 1;
        return (
          <HaloText key={l.text} x={x + (dx / d) * 11} y={y + (dy / d) * 11}>
            {l.text}
          </HaloText>
        );
      })}
    </g>
  );
}

/** segment de construction ; caché (dans le solide, ou sur une face cachée) = pointillés */
interface HelpSeg {
  a: V3;
  b: V3;
  hidden: boolean;
}

function Help({
  segs,
  marks,
  proj,
}: {
  segs: readonly HelpSeg[];
  marks: Array<[V3, V3, V3]>;
  proj: (p: V3) => XY;
}) {
  return (
    <g stroke={HELP} fill="none" strokeWidth={1.6}>
      {segs.map((h, i) => (
        <path
          key={i}
          d={seg(proj(h.a), proj(h.b))}
          strokeDasharray={h.hidden ? '4 3' : undefined}
        />
      ))}
      {marks.map((m, i) => (
        <polyline key={`m${i}`} points={pointsAttr(m.map(proj))} strokeWidth={1.2} />
      ))}
    </g>
  );
}

function MeshFigure({
  solid,
  cut,
  v,
  cam,
}: {
  solid: SectionSolid;
  cut: Cut;
  v: number;
  cam: Camera;
}) {
  const faces = useMemo(() => meshOf(solid), [solid]);
  const b = BOUNDS[solid];
  const proj = projector(cam, b.c, (SIZE / 2 - 20) / b.R, SIZE);
  const plane = planeOf(solid, cut, v);
  const pieces = sectionPieces(faces, plane);
  const poly = sectionPolygon(pieces, plane);
  const lines = edgeLines(faces, cam);
  const front = faces.filter((f) => isFront(f, cam));
  const P = namedPoints(solid);
  const labels: Label[] = [];
  const segs: HelpSeg[] = [];
  const marks: Array<[V3, V3, V3]> = [];
  const dots: V3[] = [];

  if (solid === 'cuboid') {
    for (const k of Object.keys(P)) labels.push({ text: k, at: P[k]! });
    if (cut === 'edge') {
      const { L } = DIM.cuboid;
      const I: V3 = [L - v, 0, 0];
      const J: V3 = [L, (4 * v) / 3, 0];
      labels.push({ text: 'I', at: I }, { text: 'J', at: J });
      dots.push(I, J);
      marks.push(rightMark(P.B!, I, J, 0.3));
    }
    if (cut === 'side') {
      const M: V3 = [v, 0, 0];
      labels.push({ text: 'M', at: M });
      dots.push(M);
    }
  } else if (solid === 'pyramid') {
    for (const k of ['A', 'B', 'C', 'D', 'S']) labels.push({ text: k, at: P[k]! });
    const H = DIM.pyramid.H;
    const k = (H - v) / H;
    const O2: V3 = [0, 0, v];
    labels.push({ text: 'O', at: P.O! }, { text: 'O′', at: O2 });
    for (const q of ['A', 'B', 'C', 'D']) labels.push({ text: `${q}′`, at: lerp3(P.S!, P[q]!, k) });
    // la hauteur [SO] est dans la pyramide
    segs.push({ a: P.S!, b: P.O!, hidden: true });
    dots.push(P.O!, O2);
  } else if (solid === 'cone') {
    const { r, H } = DIM.cone;
    const k = (H - v) / H;
    const A = scale(screenRight(cam), r);
    const O2: V3 = [0, 0, v];
    const A2: V3 = add(O2, scale(screenRight(cam), r * k));
    labels.push(
      { text: 'S', at: P.S! },
      { text: 'O', at: P.O! },
      { text: 'A', at: A },
      { text: 'O′', at: O2 },
      { text: 'A′', at: A2 },
    );
    // [SO] et [O′A′] sont dans le cône ; [OA] est sur la base
    const baseSeen = isFront(faces[0]!, cam);
    segs.push(
      { a: P.S!, b: P.O!, hidden: true },
      { a: P.O!, b: A, hidden: !baseSeen },
      { a: O2, b: A2, hidden: true },
    );
    dots.push(P.O!, O2);
  } else if (solid === 'cylinder') {
    const { r, h } = DIM.cylinder;
    const O = P.O!;
    labels.push({ text: 'O', at: O });
    dots.push(O);
    if (cut === 'axis') {
      const y = Math.sqrt(Math.max(0, r * r - v * v));
      const Hh: V3 = [v, 0, h];
      const M: V3 = [v, y, h];
      const N: V3 = [v, -y, h];
      labels.push(
        { text: 'M', at: M },
        { text: 'N', at: N },
        { text: 'P', at: [v, -y, 0] },
        { text: 'Q', at: [v, y, 0] },
      );
      if (v > 1e-9) {
        labels.push({ text: 'H', at: Hh });
        // [OH] et [OM] sont sur la base du haut
        const topSeen = isFront(faces[1]!, cam);
        segs.push({ a: O, b: Hh, hidden: !topSeen }, { a: O, b: M, hidden: !topSeen });
        marks.push(rightMark(Hh, O, M, 0.3));
        dots.push(Hh);
      }
    }
  }

  const center = proj(b.c);
  return (
    <>
      {poly.length >= 3 && (
        <polygon
          points={pointsAttr(poly.map(proj))}
          fill={SECTION_FILL}
          fillOpacity={0.55}
          stroke="none"
        />
      )}
      {front.map((f, i) => (
        <polygon
          key={i}
          points={pointsAttr(f.pts.map(proj))}
          fill={SOLID_FILL}
          fillOpacity={0.16}
          stroke="none"
        />
      ))}
      <path
        d={lines
          .filter((l) => l.hidden)
          .map((l) => seg(proj(l.a), proj(l.b)))
          .join('')}
        stroke={MUTED}
        strokeWidth={1.2}
        strokeDasharray="5 4"
        fill="none"
      />
      <path
        d={lines
          .filter((l) => !l.hidden)
          .map((l) => seg(proj(l.a), proj(l.b)))
          .join('')}
        stroke={INK}
        strokeWidth={1.6}
        strokeLinecap="round"
        fill="none"
      />
      <path
        d={pieces
          .filter((p) => !isFront(faces[p.face]!, cam))
          .map((p) => seg(proj(p.a), proj(p.b)))
          .join('')}
        stroke={SECTION_LINE}
        strokeWidth={1.6}
        strokeDasharray="5 4"
        fill="none"
      />
      <path
        d={pieces
          .filter((p) => isFront(faces[p.face]!, cam))
          .map((p) => seg(proj(p.a), proj(p.b)))
          .join('')}
        stroke={SECTION_LINE}
        strokeWidth={2.2}
        strokeLinecap="round"
        fill="none"
      />
      <Help segs={segs} marks={marks} proj={proj} />
      {dots.map((d, i) => {
        const [x, y] = proj(d);
        return <circle key={i} cx={x} cy={y} r={2.6} fill={INK} />;
      })}
      <Labels labels={labels} proj={proj} center={center} />
    </>
  );
}

function SphereFigure({ v, cam }: { v: number; cam: Camera }) {
  const R = DIM.sphere.R;
  const b = BOUNDS.sphere;
  const s = (SIZE / 2 - 20) / b.R;
  const proj = projector(cam, b.c, s, SIZE);
  const circle = (rho: number, z: number) =>
    Array.from({ length: 97 }, (_, k): V3 => {
      const t = (2 * Math.PI * k) / 96;
      return [rho * Math.cos(t), rho * Math.sin(t), z];
    });
  const split = (pts: V3[]) => {
    let vis = '';
    let hid = '';
    for (let k = 0; k + 1 < pts.length; k++) {
      const m = lerp3(pts[k]!, pts[k + 1]!, 0.5);
      const d = seg(proj(pts[k]!), proj(pts[k + 1]!));
      if (dot(m, cam.toward) >= 0) vis += d;
      else hid += d;
    }
    return { vis, hid };
  };
  const rho = Math.sqrt(Math.max(0, R * R - v * v));
  const sec = circle(rho, v);
  const eq = split(circle(R, 0));
  const se = split(sec);
  const O: V3 = [0, 0, 0];
  const O2: V3 = [0, 0, v];
  const M = add(O2, scale(screenRight(cam), rho));
  const c = proj(O);
  const labels: Label[] = [{ text: 'M', at: M }];
  if (v > 1e-9) labels.push({ text: 'O′', at: O2 });
  // le triangle OO′M est dans la boule : pointillés, comme la hauteur d'une pyramide
  const segs: HelpSeg[] =
    v > 1e-9
      ? [
          { a: O, b: O2, hidden: true },
          { a: O2, b: M, hidden: true },
          { a: O, b: M, hidden: true },
        ]
      : [{ a: O, b: M, hidden: true }];
  const marks = v > 1e-9 ? [rightMark(O2, O, M, 0.35)] : [];
  return (
    <>
      <circle
        cx={c[0]}
        cy={c[1]}
        r={R * s}
        fill={SOLID_FILL}
        fillOpacity={0.16}
        stroke={INK}
        strokeWidth={1.6}
      />
      <polygon
        points={pointsAttr(sec.map(proj))}
        fill={SECTION_FILL}
        fillOpacity={0.55}
        stroke="none"
      />
      <path d={eq.hid} stroke={MUTED} strokeWidth={1.1} strokeDasharray="5 4" fill="none" />
      <path d={eq.vis} stroke={MUTED} strokeWidth={1.1} fill="none" />
      <path d={se.hid} stroke={SECTION_LINE} strokeWidth={1.6} strokeDasharray="5 4" fill="none" />
      <path d={se.vis} stroke={SECTION_LINE} strokeWidth={2.2} fill="none" />
      <Help segs={segs} marks={marks} proj={proj} />
      {[O, O2].map((p, i) => {
        const [x, y] = proj(p);
        return <circle key={i} cx={x} cy={y} r={2.6} fill={INK} />;
      })}
      <HaloText
        x={c[0] - 12}
        y={c[1] + 4}
        fontSize={13}
        fontWeight={700}
        fill={INK}
        textAnchor="middle"
      >
        O
      </HaloText>
      <Labels labels={labels} proj={proj} center={c} />
    </>
  );
}

/** la section en vraie grandeur, à la même échelle pour un même solide */
function TrueShape({ shape, solid }: { shape: Shape; solid: SectionSolid }) {
  const W = 204;
  const Hh = 176;
  const max = solid === 'sphere' ? 10 : 6;
  const s = 116 / max;
  // marge à droite pour la cote de hauteur
  const cx = 86;
  const cy = 72;
  return (
    <svg
      viewBox={`0 0 ${W} ${Hh}`}
      className="h-auto w-[204px] shrink-0"
      role="img"
      aria-label={`Section en vraie grandeur, à l’échelle : ${
        shape.kind === 'rect'
          ? shape.hLabel
            ? `${shape.wLabel} sur ${shape.hLabel}`
            : `carré, ${shape.wLabel}`
          : `disque, ${shape.rLabel}`
      }`}
    >
      {shape.kind === 'rect' ? (
        <>
          <rect
            x={cx - (shape.w * s) / 2}
            y={cy - (shape.h * s) / 2}
            width={shape.w * s}
            height={shape.h * s}
            fill={SECTION_FILL}
            fillOpacity={0.55}
            stroke={SECTION_LINE}
            strokeWidth={2}
          />
          <text x={cx} y={cy + (shape.h * s) / 2 + 15} fontSize={12} fill={INK} textAnchor="middle">
            {shape.wLabel}
          </text>
          {shape.hLabel && (
            <text
              x={cx + (shape.w * s) / 2 + 4}
              y={cy}
              fontSize={12}
              fill={INK}
              textAnchor="start"
              dominantBaseline="central"
            >
              {shape.hLabel}
            </text>
          )}
        </>
      ) : (
        <>
          <circle
            cx={cx}
            cy={cy}
            r={shape.r * s}
            fill={SECTION_FILL}
            fillOpacity={0.55}
            stroke={SECTION_LINE}
            strokeWidth={2}
          />
          <path d={seg([cx, cy], [cx + shape.r * s, cy])} stroke={INK} strokeWidth={1.4} />
          <circle cx={cx} cy={cy} r={2.4} fill={INK} />
          <text x={cx} y={cy + shape.r * s + 15} fontSize={12} fill={INK} textAnchor="middle">
            {shape.rLabel}
          </text>
        </>
      )}
      <text x={cx} y={Hh - 4} fontSize={12} fill="var(--color-ink-500)" textAnchor="middle">
        vraie grandeur, à l’échelle
      </text>
    </svg>
  );
}

export function SolidSectionsTool() {
  const [solid, setSolid] = useState<SectionSolid>('pyramid');
  const [cut, setCut] = useState<Cut>('base');
  const [vals, setVals] = useState<Record<string, number>>({});
  const orbit = useOrbit({ yaw: -1.07, pitch: 0.42 });
  const param = paramOf(solid, cut);
  const key = `${solid}:${cut}`;
  const v = vals[key] ?? param.initial;
  const info = sectionInfo(solid, cut, v);
  const cutLabel = CUTS[solid].find((c) => c.id === cut)!.label;
  // « par un plan parallèle à la base », « par un plan qui coupe la boule »
  const byPlane = cutLabel.startsWith('Plan')
    ? `par un ${cutLabel.toLowerCase()}`
    : `par un plan ${cutLabel.toLowerCase()}`;
  const cuts = CUTS[solid];
  return (
    <div className="space-y-3">
      <div role="group" aria-label="Solide" className="flex flex-wrap gap-2">
        {SECTION_SOLIDS.map((s) => (
          <ToggleChip
            key={s.id}
            on={s.id === solid}
            onClick={() => {
              setSolid(s.id);
              setCut(CUTS[s.id][0]!.id);
            }}
          >
            {s.label}
          </ToggleChip>
        ))}
      </div>
      {cuts.length > 1 && (
        <div role="group" aria-label="Plan de coupe" className="flex flex-wrap gap-2">
          {cuts.map((c) => (
            <ToggleChip key={c.id} on={c.id === cut} onClick={() => setCut(c.id)}>
              {c.label}
            </ToggleChip>
          ))}
        </div>
      )}
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={frenchSpaces(`Section ${OF[solid]} ${byPlane} : ${info.name}.`)}
        className={cn(
          'border-ink-100 w-full max-w-[360px] cursor-grab rounded-2xl border',
          FIGURE_BG_CLASS,
          ORBIT_FOCUS_CLASS,
        )}
        {...orbit.svgProps}
      >
        {solid === 'sphere' ? (
          <SphereFigure v={v} cam={orbit.cam} />
        ) : (
          <MeshFigure solid={solid} cut={cut} v={v} cam={orbit.cam} />
        )}
      </svg>
      <OrbitButtons orbit={orbit} />
      <ParamSlider
        name={param.label}
        ariaLabel={param.label}
        value={v}
        min={param.min}
        max={param.max}
        step={param.step}
        format={(x) => `${formatNumber(x, 2)} cm`}
        onChange={(x) => setVals({ ...vals, [key]: x })}
      />
      <div className="flex flex-wrap items-start gap-3">
        <TrueShape shape={info.shape} solid={solid} />
        <div className="min-w-[180px] flex-1 space-y-2">
          <p className="text-ink-900 text-sm font-bold" aria-live="polite">
            {frenchSpaces(`La section ${OF[solid]} est ${info.name}.`)}
          </p>
          <Note live={false}>
            {info.lines.map((l) => (
              <span key={l} className="mb-1 block last:mb-0">
                {frenchSpaces(l)}
              </span>
            ))}
          </Note>
        </div>
      </div>
    </div>
  );
}
